import React from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { Broker, Listing } from '@/types'
import config from '@/config'
import { breakpoints, colors } from '@/styles'
import { OldPlanName } from '@/features/Pricing/pricing-plans'
import ListingSection from '@/components/listing-section'

import BrokerInfo from './broker-info'
import BrokerInfoBlurred from './broker-info-blurred'
import LandlordInfo from './landlord-info'
import LandlordInfoBlurred from './landlord-info-blurred'

const isDev = config.BUILD_ENV === 'dev'

type Props = {
  listings: Listing
  landlord: {
    id: string
    picture: string
    company: {
      name: string
      address: string
      state: string
      city: string
    }
    broker: Broker
  }
  externalInfo?: {
    realtor_display_info: boolean
    realtor_name: string
    realtor_logo_url: string
    realtor_external_id: string
  }
  isLandlordInfoPresent: boolean
  companyData: {
    id: string
    name: string
    account_type: OldPlanName
  }
  isListingLocked: boolean
}

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  background: ${colors.background};
  border-radius: 8px;
  padding: 0 24px;

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 24px 0;

    :not(:last-child) {
      border-bottom: 1px solid #eee;
    }

    @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
      align-items: flex-start;
    }
  }
`

const VIP_COMPANIES: Record<string, string> = {
  [isDev ? 14 : 817]: '/company/wbrothers',
  [isDev ? 15 : 25332]: '/company/lbrothers',
  [isDev ? 168 : 10]: '/company/prompton',
  [isDev ? 1 : 94737]: '/company/homelifepropertymanagementbc'
}

const ListingContact = ({
  listings,
  landlord,
  isLandlordInfoPresent,
  companyData,
  isListingLocked,
  externalInfo
}: Props) => {
  const { t } = useTranslation()
  const broker = landlord?.broker
  const { id, name, account_type } = companyData
  const hideCompanyInfo = !name
  const showLink = account_type === 'business' || account_type === 'family'
  const genericLink = showLink && id ? `/company/${id}` : undefined
  const baseLink = VIP_COMPANIES[id] || genericLink
  const companyLink =
    externalInfo?.realtor_display_info && externalInfo?.realtor_external_id
      ? `/company/${id}?ext=${externalInfo?.realtor_external_id}`
      : baseLink

  return (
    <ListingSection
      title={t('listing:b.listing.view.listing_contact.text', 'Contact')}
      customGap="0px"
    >
      <InnerWrapper>
        {isListingLocked ? (
          <>
            <LandlordInfoBlurred
              landlord={landlord}
              isLandlordInfoPresent={isLandlordInfoPresent}
              listings={listings}
              companyLink={companyLink}
              hideCompanyInfo={hideCompanyInfo}
            />
            <BrokerInfoBlurred broker={broker} />
          </>
        ) : (
          <>
            <LandlordInfo
              landlord={landlord}
              isLandlordInfoPresent={isLandlordInfoPresent}
              listings={listings}
              companyLink={companyLink}
              hideCompanyInfo={hideCompanyInfo}
              externalInfo={externalInfo}
            />
            <BrokerInfo broker={broker} />
          </>
        )}
      </InnerWrapper>
    </ListingSection>
  )
}

export default ListingContact
